export const EXECUTIVE_PDF_LAYOUT = {
  format: 'a3' as const,
  orientation: 'landscape' as const,
  pageWidthMm: 420,
  pageHeightMm: 297,
  marginMm: 8,
  scale: 2,
  // Native Chromium typography avoids html2canvas's reconstructed font
  // baselines, which are affected by Tailwind preflight's block image rule.
  foreignObjectRendering: true,
  surfaceWidthPx: 1584,
  backgroundColor: '#F7F8FA'
};

export type ExecutivePdfPlacement = {
  scale: number;
  widthMm: number;
  heightMm: number;
  xMm: number;
  yMm: number;
};

export type ExecutivePdfAlignmentMeasurement = {
  kind: string;
  horizontalErrorPx: number;
  verticalErrorPx: number;
  textTopPx: number;
};

export type ExecutivePdfAlignmentResult = {
  measurements: ExecutivePdfAlignmentMeasurement[];
  maxHorizontalErrorPx: number;
  maxVerticalErrorPx: number;
  cardTitleBaselineSpreadPx: number;
};

const center = (start: number, size: number): number => start + size / 2;

export const assertExecutivePdfAlignment = (
  root: ParentNode,
  tolerancePx = 2
): ExecutivePdfAlignmentResult => {
  const cards = root.querySelectorAll('[data-pdf-summary-card]');
  const findingGroups = root.querySelectorAll('[data-pdf-finding-group]');
  if (cards.length !== 8) throw new Error('PDF_ALIGNMENT_CARD_COUNT_INVALID');
  if (findingGroups.length !== 3) throw new Error('PDF_ALIGNMENT_FINDING_COUNT_INVALID');

  const containers = Array.from(root.querySelectorAll<HTMLElement>('[data-pdf-align-container]'));
  const measurements = containers.map((container): ExecutivePdfAlignmentMeasurement => {
    const text = container.querySelector<HTMLElement>('[data-pdf-align-text]');
    if (!text) throw new Error('PDF_ALIGNMENT_TEXT_MISSING');
    const containerRect = container.getBoundingClientRect();
    const textRect = text.getBoundingClientRect();
    const horizontalErrorPx = center(textRect.left, textRect.width) - center(containerRect.left, containerRect.width);
    const verticalErrorPx = center(textRect.top, textRect.height) - center(containerRect.top, containerRect.height);
    if (Math.abs(horizontalErrorPx) > tolerancePx || Math.abs(verticalErrorPx) > tolerancePx) throw new Error('PDF_ALIGNMENT_TOLERANCE_EXCEEDED');
    if (
      textRect.left < containerRect.left - tolerancePx
      || textRect.right > containerRect.right + tolerancePx
      || textRect.top < containerRect.top - tolerancePx
      || textRect.bottom > containerRect.bottom + tolerancePx
      || text.scrollWidth > container.clientWidth + tolerancePx
    ) throw new Error('PDF_ALIGNMENT_TEXT_CLIPPED');
    return {
      kind: container.getAttribute('data-pdf-align-kind') ?? 'unknown',
      horizontalErrorPx,
      verticalErrorPx,
      textTopPx: textRect.top
    };
  });

  const cardTitles = measurements.filter((measurement) => measurement.kind === 'card-title');
  if (cardTitles.length !== 8) throw new Error('PDF_ALIGNMENT_CARD_TITLE_COUNT_INVALID');
  const titleTops = cardTitles.map((measurement) => measurement.textTopPx);
  const cardTitleBaselineSpreadPx = Math.max(...titleTops) - Math.min(...titleTops);
  if (cardTitleBaselineSpreadPx > tolerancePx) throw new Error('PDF_ALIGNMENT_BASELINE_SPREAD_EXCEEDED');

  return {
    measurements,
    maxHorizontalErrorPx: Math.max(...measurements.map((measurement) => Math.abs(measurement.horizontalErrorPx))),
    maxVerticalErrorPx: Math.max(...measurements.map((measurement) => Math.abs(measurement.verticalErrorPx))),
    cardTitleBaselineSpreadPx
  };
};

export const calculateExecutivePdfPlacement = (
  canvasWidth: number,
  canvasHeight: number
): ExecutivePdfPlacement => {
  if (canvasWidth <= 0 || canvasHeight <= 0) throw new Error('PDF_CANVAS_INVALID');
  const availableWidth = EXECUTIVE_PDF_LAYOUT.pageWidthMm - EXECUTIVE_PDF_LAYOUT.marginMm * 2;
  const availableHeight = EXECUTIVE_PDF_LAYOUT.pageHeightMm - EXECUTIVE_PDF_LAYOUT.marginMm * 2;
  const scale = Math.min(availableWidth / canvasWidth, availableHeight / canvasHeight);
  const widthMm = canvasWidth * scale;
  const heightMm = canvasHeight * scale;
  return {
    scale,
    widthMm,
    heightMm,
    xMm: (EXECUTIVE_PDF_LAYOUT.pageWidthMm - widthMm) / 2,
    yMm: (EXECUTIVE_PDF_LAYOUT.pageHeightMm - heightMm) / 2
  };
};

const nextFrame = (): Promise<void> => new Promise((resolve) => requestAnimationFrame(() => resolve()));

export const prepareExecutivePdfClone = (clonedDocument: Document): void => {
  const root = clonedDocument.querySelector<HTMLElement>('[data-pdf-export-surface]');
  if (!root) throw new Error('PDF_ROOT_NOT_FOUND');
  clonedDocument.documentElement.style.setProperty('background-color', EXECUTIVE_PDF_LAYOUT.backgroundColor, 'important');
  clonedDocument.body.style.setProperty('background-color', EXECUTIVE_PDF_LAYOUT.backgroundColor, 'important');
  Object.assign(root.style, { position: 'static', left: 'auto', top: 'auto', margin: '0' });
  const host = root.closest<HTMLElement>('[data-pdf-offscreen-host]');
  // Native SVG capture needs the dedicated cloned surface at the origin,
  // independent of the interactive page's scroll or offscreen positioning.
  if (host) Object.assign(host.style, { position: 'fixed', left: '0', top: '0' });
};

export const waitForExecutivePdfSurface = async (root: HTMLElement): Promise<void> => {
  if (document.fonts?.ready) await document.fonts.ready;
  const images = Array.from(root.querySelectorAll<HTMLImageElement>('img'));
  await Promise.all(images.map((image) => image.complete
    ? Promise.resolve()
    : new Promise<void>((resolve) => {
      image.addEventListener('load', () => resolve(), { once: true });
      image.addEventListener('error', () => resolve(), { once: true });
    })));

  let previous = '';
  let stableFrames = 0;
  for (let attempt = 0; attempt < 8 && stableFrames < 2; attempt += 1) {
    await nextFrame();
    const current = `${root.scrollWidth}x${root.scrollHeight}`;
    stableFrames = current === previous ? stableFrames + 1 : 0;
    previous = current;
  }
  if (stableFrames < 2) throw new Error('PDF_LAYOUT_UNSTABLE');
};

const UNSUPPORTED_PDF_COLOR = /\b(?:oklch|oklab|color)\(/i;

export const validateExecutivePdfSurface = (root: HTMLElement): { width: number; height: number; rows: number } => {
  const rows = root.querySelectorAll('[data-pdf-vp-row]');
  const footer = root.querySelector<HTMLElement>('[data-pdf-footer]');
  const lastRow = root.querySelector<HTMLElement>('[data-pdf-last-row]');
  if (rows.length !== 14) throw new Error('PDF_EXECUTIVE_ROW_COUNT_INVALID');
  if (!footer || !lastRow) throw new Error('PDF_REQUIRED_CONTENT_MISSING');
  const rootRect = root.getBoundingClientRect();
  if (footer.getBoundingClientRect().bottom > rootRect.bottom + 1) throw new Error('PDF_FOOTER_CLIPPED');
  if (lastRow.getBoundingClientRect().bottom > rootRect.bottom + 1) throw new Error('PDF_LAST_ROW_CLIPPED');
  if (root.scrollWidth > root.clientWidth + 1) throw new Error('PDF_HORIZONTAL_OVERFLOW');
  assertExecutivePdfAlignment(root);

  const elements = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];
  for (const element of elements) {
    const computed = getComputedStyle(element);
    const colors = [computed.color, computed.backgroundColor, computed.borderTopColor, computed.borderRightColor, computed.borderBottomColor, computed.borderLeftColor];
    if (colors.some((value) => UNSUPPORTED_PDF_COLOR.test(value))) throw new Error('PDF_UNSUPPORTED_COLOR');
  }
  return { width: root.scrollWidth, height: root.scrollHeight, rows: rows.length };
};
